import type { Disposable } from "@/shared/contracts/lifecycle";
import type { HoverPointerModel } from "../input/hoverPointerModel";
import { resolveHoverActive } from "../input/resolveHoverActive";

interface HoverIntentControllerOptions {
  readonly target: HTMLElement;
  // Returning false means the authoritative consumer rejected the proposed
  // hover state. Rejected state must remain eligible for a later retry.
  readonly onHoverChange: (isHovering: boolean) => boolean | void;
  readonly pointerModel: HoverPointerModel;
  readonly initiallyEnabled?: boolean;
}

export class HoverIntentController implements Disposable {
  private readonly target: HTMLElement;
  private readonly onHoverChange: (isHovering: boolean) => boolean | void;
  private readonly pointerModel: HoverPointerModel;
  private readonly unsubscribePointer: () => void;
  private isPointerInside = false;
  private isTargetFocused = false;
  private lastEmittedHover = false;
  private isEnabled: boolean;

  private readonly handleFocus = (): void => {
    this.isTargetFocused = true;
    this.syncHoverIntent();
  };

  private readonly handleBlur = (): void => {
    this.isTargetFocused = false;
    this.syncHoverIntent();
  };

  constructor(options: HoverIntentControllerOptions) {
    this.target = options.target;
    this.onHoverChange = options.onHoverChange;
    this.pointerModel = options.pointerModel;
    this.isEnabled = options.initiallyEnabled ?? true;

    this.target.addEventListener("focus", this.handleFocus);
    this.target.addEventListener("blur", this.handleBlur);

    this.unsubscribePointer = this.pointerModel.subscribe(() => {
      this.refreshFromPointer();
    });
    this.refreshFromPointer();
  }

  // forceEmit is for reset/render sync points where model hover state may have
  // been cleared while pointer-inside state did not change.
  refresh(options?: { forceEmit?: boolean }): void {
    this.refreshFromPointer(options);
  }

  clearPointerHover(): void {
    if (!this.isPointerInside) {
      return;
    }
    this.isPointerInside = false;
    this.syncHoverIntent();
  }

  // Target visibility/unlock is an input-boundary concern. Disabling always
  // republishes non-hover so authoritative state cannot retain a stale target;
  // the owner refreshes from the saved pointer snapshot after enabling.
  setEnabled(enabled: boolean): void {
    if (enabled === this.isEnabled) {
      return;
    }
    this.isEnabled = enabled;
    if (enabled) {
      return;
    }
    this.isPointerInside = false;
    this.syncHoverIntent({ force: true });
  }

  dispose(): void {
    this.target.removeEventListener("focus", this.handleFocus);
    this.target.removeEventListener("blur", this.handleBlur);
    this.unsubscribePointer();
  }

  private refreshFromPointer(options?: { forceEmit?: boolean }): void {
    // Targets inside an `inert` or hidden subtree must not claim hover from
    // rect intersection — they are visually and semantically blocked even
    // though pointerModel still tracks position.
    if (this.isTargetBlocked()) {
      this.isPointerInside = false;
      this.syncHoverIntent({ force: options?.forceEmit });
      return;
    }
    const snapshot = this.pointerModel.getSnapshot();
    const rect = this.target.getBoundingClientRect();
    const next = resolveHoverActive(snapshot, rect);
    this.isPointerInside = next;
    // Always offer the current intent to syncHoverIntent. Accepted unchanged
    // state is deduplicated there, while a previously rejected activation has
    // lastEmittedHover=false and is therefore retried on pointer republish.
    this.syncHoverIntent({ force: options?.forceEmit });
  }

  private syncHoverIntent(options?: { force?: boolean }): void {
    const next = this.isTargetBlocked()
      ? false
      : this.isPointerInside || this.isTargetFocused;
    if (!options?.force && next === this.lastEmittedHover) {
      return;
    }
    // Commit provisionally to make a re-entrant render of the same state a
    // no-op. If authority rejects it, record the opposite accepted state so a
    // later refresh can retry instead of latching the rejected proposal.
    this.lastEmittedHover = next;
    const accepted = this.onHoverChange(next);
    if (accepted === false && this.lastEmittedHover === next) {
      this.lastEmittedHover = !next;
    }
  }

  private isTargetBlocked(): boolean {
    return (
      !this.isEnabled ||
      this.target.closest("[inert]") !== null ||
      this.target.closest("[hidden]") !== null
    );
  }
}
